<template>
  <div>
    <section class="pages-card">
      <div class="pages-head">
        <div class="pages-head-main">
          <div class="brand-eyebrow">Solutions by page</div>
          <h2 class="pages-title">{{ headline }}</h2>
          <p class="pages-standfirst">Pick a page to review its markup. Each one stands alone — paste them in any order.</p>
        </div>
        <div class="pages-chips">
          <span v-if="countsLabel" class="pages-chip">{{ countsLabel }}</span>
          <span v-if="readLabel" class="pages-chip">Read {{ readLabel }}</span>
        </div>
      </div>

      <div class="pages-grid">
        <button
          v-for="page in pages"
          :key="page.index"
          type="button"
          class="page-card"
          :class="{ 'is-active': page.index === activeIndex }"
          @click="$emit('select-page', page.index)"
        >
          <span class="page-card-top">
            <span class="page-num" :class="{ 'is-active': page.index === activeIndex }">{{ page.index + 1 }}</span>
            <span class="page-action" :class="`action-${page.action}`">{{ page.actionLabel }}</span>
            <span class="page-severity">{{ page.severityLabel }}</span>
          </span>
          <span class="page-path">{{ page.path }}</span>
          <span class="page-meta">{{ page.meta }}</span>
        </button>
      </div>
    </section>

    <div class="page-nav">
      <span class="nav-counter">Page {{ activeIndex + 1 }} of {{ pages.length }}</span>
      <span class="nav-path">{{ activePath }}</span>
      <span class="nav-spacer"></span>
      <button type="button" class="nav-btn" :disabled="!hasPrev" @click="$emit('select-page', activeIndex - 1)">
        ← {{ prevLabel }}
      </button>
      <button type="button" class="nav-btn" :disabled="!hasNext" @click="$emit('select-page', activeIndex + 1)">
        {{ nextLabel }} →
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SchemaSolutionPageTabs',
  props: {
    pages: {
      type: Array,
      default: () => []
    },
    activeIndex: {
      type: Number,
      default: 0
    },
    countsLabel: {
      type: String,
      default: ''
    },
    readLabel: {
      type: String,
      default: ''
    }
  },
  emits: ['select-page'],
  computed: {
    headline() {
      const count = this.pages.length
      if (count === 1) return 'One block, for one page'
      return `${this.countWord(count)} blocks, one per page`
    },
    activePage() {
      return this.pages[this.activeIndex] || null
    },
    activePath() {
      return this.activePage ? this.activePage.path : ''
    },
    hasPrev() {
      return this.activeIndex > 0
    },
    hasNext() {
      return this.activeIndex < this.pages.length - 1
    },
    prevLabel() {
      return this.hasPrev ? this.pages[this.activeIndex - 1].path : 'No earlier page'
    },
    nextLabel() {
      return this.hasNext ? this.pages[this.activeIndex + 1].path : 'No further page'
    }
  },
  methods: {
    countWord(count) {
      const words = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']
      return words[count] || String(count)
    }
  }
}
</script>

<style scoped>
.pages-card {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
}

.pages-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  row-gap: 12px;
  flex-wrap: wrap;
}

.pages-head-main {
  min-width: 0;
}

.pages-title {
  font-family: 'Lora', serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 4px;
}

.pages-standfirst {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--brand-ink-800);
  max-width: 62ch;
}

.pages-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pages-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--brand-sand-100);
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-ink-800);
  white-space: nowrap;
}

.pages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.page-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  padding: 12px 13px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.page-card.is-active {
  background: #f2f7fc;
  border-color: var(--brand-ocean-600);
  box-shadow: 0 0 0 3px rgba(33, 105, 168, 0.18);
}

.page-card-top {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  white-space: nowrap;
}

.page-num {
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.page-num.is-active {
  background: var(--brand-ocean-600);
  color: #fff;
}

.page-action {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.action-add {
  background: var(--brand-teal-100);
  color: #127a6a;
}

.action-replace {
  background: var(--brand-amber-100);
  color: #92610a;
}

.action-unknown {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.page-severity {
  margin-left: auto;
  font-size: 11px;
  color: #8496bc;
}

.page-path {
  display: block;
  width: 100%;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-ink-950);
  overflow-wrap: anywhere;
  margin: 9px 0 5px;
}

.page-meta {
  display: block;
  width: 100%;
  font-size: 11.5px;
  color: var(--brand-ink-800);
}

.page-nav {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 0;
  margin-bottom: 16px;
  background: var(--brand-sand-50);
  border-bottom: 1px solid var(--brand-sand-300);
  white-space: nowrap;
}

.nav-counter {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8496bc;
}

.nav-path {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.nav-spacer {
  flex: 1 1 40px;
}

.nav-btn {
  display: inline-block;
  border-radius: 999px;
  padding: 6px 13px;
  font-size: 12.5px;
  font-weight: 600;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  color: var(--brand-ocean-600);
  cursor: pointer;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-btn:disabled {
  color: #8496bc;
  cursor: not-allowed;
}

@media (max-width: 800px) {
  .page-nav {
    white-space: normal;
  }
}
</style>
